
// application/problem+json (API spec §7) surfaced as a typed error: `code` is the
// machine-matchable discriminant, `errors` the per-field detail of 422s.

export interface FieldError {
    field: string;
    message: string;
}



export class TVirtApiError extends Error {

    readonly status: number;
    readonly code: string;
    readonly detail: string | undefined;
    readonly errors: FieldError[];



    constructor(status: number, body: Record<string, unknown>) {
        const detail = typeof body["detail"] === "string" ? body["detail"] : undefined;
        const title = typeof body["title"] === "string" ? body["title"] : `HTTP ${status}`;
        // The per-field detail of 422s rides the message too: a bare "Validation
        // failed" helps nobody at a CLI prompt or in a webui alert (found 2026-07-13,
        // an admission refusal surfacing detail-lessly in the UI).
        const fields = Array.isArray(body["errors"])
            ? (body["errors"] as FieldError[]).map((e) => `${e.field}: ${e.message}`).join("; ")
            : "";
        super(`${title}${detail !== undefined ? `: ${detail}` : ""}${fields !== "" ? ` — ${fields}` : ""}`);
        this.name = "TVirtApiError";
        this.status = status;
        this.code = typeof body["code"] === "string" ? body["code"] : "unknown";
        this.detail = detail;
        this.errors = Array.isArray(body["errors"]) ? (body["errors"] as FieldError[]) : [];
    }

}



// A task reached a terminal phase other than succeeded.
export class TVirtTaskError extends Error {

    readonly task: Record<string, unknown>;



    constructor(task: Record<string, unknown>) {
        const error = task["error"] as { title?: string; detail?: string } | undefined;
        const cause = error?.title !== undefined
            ? `: ${error.title}${error.detail !== undefined ? ` — ${error.detail}` : ""}`
            : "";
        super(`task ${String(task["id"])} ${String(task["phase"])}${cause}`);
        this.name = "TVirtTaskError";
        this.task = task;
    }

}



// The wait BUDGET ran out — and that is ALL it says. The task is still
// queued or running for what the client knows; the caller must not present
// this as a failure (Luca, 2026-08-06: a vm.stop stuck behind a dead worker
// outlived the console's budget, the console said "Azione non riuscita", the
// stop then succeeded — a client timeout carries no certainty about the
// server). Distinct type so callers can branch: keep waiting, or report
// "outcome unknown, still in flight" — never "failed". `lastTask` is the
// freshest snapshot the poller saw, for resuming or rendering progress.
export class TVirtWaitTimeoutError extends Error {

    readonly ref: string;
    readonly lastTask: Record<string, unknown> | undefined;



    constructor(ref: string, lastTask?: Record<string, unknown>) {
        super(`the wait budget for ${ref} ran out — the task is still in flight, the outcome is UNKNOWN`);
        this.name = "TVirtWaitTimeoutError";
        this.ref = ref;
        this.lastTask = lastTask;
    }

}
